import {
  Box,
  Button,
  ButtonProps,
  Icon,
  SystemStyleObject,
  Text,
} from '@chakra-ui/react'
import React, { forwardRef, useState } from 'react'
import { Size } from '~/types/ThemeTypes'

import { MangoButtonVariant } from './MangoButtonStyle'
import { useMangoButtonStyleConfig } from './useMangoButtonStyleConfig'

export interface MangoButtonProps extends Omit<
  ButtonProps,
  'children' | 'size' | 'onClick' | 'variant' | 'leftIcon' | 'rightIcon'
> {
  disabled?: boolean
  isLoading?: boolean
  leftIcon?: React.ComponentType<any>
  leftIconProps?: SystemStyleObject | Record<string, any>
  rightIcon?: React.ComponentType<any>
  rightIconProps?: SystemStyleObject | Record<string, any>
  leftNode?: React.ReactNode
  rightNode?: React.ReactNode
  size?:
    Size.md | Size.sm | { base?: Size.md | Size.sm; md?: Size.md | Size.sm }
  variant?: MangoButtonVariant
  children?: string | React.ReactNode
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  onHoverStateChange?: (isHovered: boolean) => void
  iconStrokeWidth?: string
}
/**
 * The standard button used throughout the application.
 * @param text The text to display on the button.
 * @param disabled Whether the button is disabled. Defaults to false.
 * @param size The size of the button. Defaults to Medium.
 * @param variant The variant of the button. Defaults to Primary.
 * @param leftIcon The icon to display on the left side of the button.
 * @param rightIcon The icon to display on the right side of the button.
 * @param leftContent The content to display on the left side of the button. Overrides leftIcon.
 * @param rightContent The content to display on the right side of the button. Overrides rightIcon.
 * @param onClick The function to call when the button is clicked.
 * @returns
 */
export const MangoButton = forwardRef<HTMLButtonElement, MangoButtonProps>(
  (
    {
      disabled = false,
      leftIcon,
      leftIconProps,
      rightIcon,
      rightIconProps,
      leftNode,
      rightNode,
      size = Size.md,
      variant = MangoButtonVariant.Default_300,
      children,
      onClick,
      onHoverStateChange,
      iconStrokeWidth = '2px',
      ...rest
    }: MangoButtonProps,
    ref,
  ) => {
    const [_, setHoverState] = useState(false)
    const handleMouseEnter = () => {
      setHoverState(true)
      onHoverStateChange?.(true)
    }
    const handleMouseLeave = () => {
      setHoverState(false)
      onHoverStateChange?.(false)
    }

    const styles = useMangoButtonStyleConfig({
      variant,
      size,
    })

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return
      onClick?.(event)
    }

    const getLeftContent = () => {
      if (!leftIcon && !leftNode) {
        return null
      }
      const content =
        leftNode ??
        (leftIcon ? (
          <Icon
            {...styles.icon}
            {...leftIconProps}
            css={{
              '& path': { strokeWidth: iconStrokeWidth },
            }}
          >
            {React.createElement(leftIcon)}
          </Icon>
        ) : null)
      return <Box css={styles.nodeContainer}>{content}</Box>
    }

    const getRightContent = () => {
      if (!rightIcon && !rightNode) {
        return null
      }

      const content =
        rightNode ??
        (rightIcon ? (
          <Icon
            {...styles.icon}
            {...rightIconProps}
            css={{
              '& path': { strokeWidth: iconStrokeWidth },
            }}
          >
            {React.createElement(rightIcon)}
          </Icon>
        ) : null)
      return <Box css={styles.nodeContainer}>{content}</Box>
    }

    return (
      <Button
        data-group
        {...styles.button}
        onClick={handleClick}
        aria-disabled={disabled}
        ref={ref}
        {...rest}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {getLeftContent()}
        {children && <Text css={styles.text}>{children}</Text>}
        {getRightContent()}
      </Button>
    )
  },
)
